<template>
  <div
    :class="{ 'q-mx-auto': $register }"
    :style="{ 'max-width': $register ? '800px' : undefined }"
    data-test-id="select-users"
  >
    <member-form
      v-show="showMemberForm"
      ref="memberForm"
      :embedded-in-model-table="false"
      simplify
      @apply-changes="memberFormApplyChanges()"
      @add-model="memberFormApplyChanges()"
    />
    <div
      class="column"
    >
      <div
        v-if="showCurrentSelectedSeatInfo && selectedSeat && combinedTicketSelection"
        class="row justify-start items-center q-gutter-sm q-pa-sm"
      >
        <div class="text-responsive-grey text-body1">
          {{ $t('checkout.infoTitles.seatsSelected') }}:
        </div>
        <div class="text-body1 text-capitalize">
          {{ selectedSeat }}
        </div>
      </div>
      <div
        v-if="$register"
        class="col-auto q-mt-md q-mb-lg row justify-center q-gutter-md"
      >
        <q-btn
          v-if="!user"
          outline
          color="primary"
          :label="$t('user.actions.select')"
          @click="$emit('find-user')"
        />
        <div v-if="user && user.membership && !user.membership.effective && !order.purchasingDayPass">
          <q-btn
            outline
            color="positive"
            :label="$t('membership.actions.activateMembership')"
            @click="$emit('modify-membership')"
          />
        </div>
        <div v-if="user && user.membership && user.membership.state === 'pending' && !order.purchasingDayPass">
          <q-btn
            color="negative"
            outline
            :label="$t('membership.actions.fixPendingCharge')"
            @click="$emit('fix-pending-membership')"
          />
        </div>
        <q-btn
          v-if="addDayPassLoading || (!user || !user.membership || user.membership.state === 'inactive' || user.membershipType.free) && currentSite.dayPassEnabled && !order.purchasingDayPass"
          :outline="order.ticketsRemaining(showing) > 0"
          :flat="order.ticketsRemaining(showing) <= 0"
          color="accent"
          :label="order.ticketsRemaining(showing) <= 0 ? $t('status.unavailable') : $t('ticket.actions.addDayPass')"
          :loading="addDayPassLoading"
          :disabled="order.ticketsRemaining(showing) <= 0 || ticketLimitReached"
          @click="addDayPass()"
        />
        <q-tooltip
          v-if="ticketLimitReached"
          :offset="[10, 10]"
        >
          {{ $t('showing.noMoreTicketsAvailableForShowing') }}
        </q-tooltip>
      </div>
      <q-banner
        v-if="$consumer && currentSite.hasBookingFees && !!currentSite.bookingFeeText"
        class="bg-responsive-grey-1 text-responsive-grey-7 text-left text-subtitle q-pa-md"
        :class="$q.screen.lt.sm && !!currentSite.consumerConfig.showTicketFirstCheckoutFlow ? 'q-mt-sm' : undefined"
        rounded
        dense
      >
        <template #avatar>
          <q-icon
            name="info"
            color="secondary"
            size="xs"
          />
        </template>
        {{ currentSite.bookingFeeText }}
        <q-tooltip>{{ $t('checkout.discountsDisclaimer') }} </q-tooltip>
      </q-banner>
      <div
        v-if="order.user"
        class="member-tickets-container"
        :class="{
          'q-mb-lg': !combinedTicketSelection,
          'q-mt-md': $q.screen.gt.sm
        }"
      >
        <q-banner
          v-if="order.isMaxTicketLimitReached(showing)"
          class="bg-warning self-center q-ma-none q-pa-none"
        >
          <div class="text-center">
            <q-icon
              name="warning"
              color="negative"
            />
            {{ $t('pages.selectTickets.maximumTicketsPerOrderMessage', { limit: showing.ticketLimit }) }}
          </div>
        </q-banner>
        <q-inner-loading
          v-if="order.user && order.user.loading"
          :showing="true"
        />

        <template v-else>
          <div
            v-if="!combinedTicketSelection"
            class="col-auto text-label text-left text-h6 q-px-sm"
            :class="$q.screen.lt.sm ? 'q-my-sm' : 'q-my-md'"
          >
            {{ $t('pages.selectTickets.greeting', { name: order.user.firstName, membershipTypesAvailable }) }}
          </div>
          <div
            v-if="$register || !combinedTicketSelection"
            class="text-h6 text-left bg-responsive-grey-2"
            :class="$q.screen.lt.sm ? 'q-pa-sm' : 'q-pa-md'"
          >
            {{ $tc(membershipTypesAvailable ? 'ticket.memberTicket' : 'ticket.ticket', 2) }}
          </div>
          <div
            v-if="showing.loading"
            class="full-width items-center text-center q-list q-list--bordered q-list--separator"
          >
            <q-spinner size="lg" />
          </div>
          <q-list
            v-else
            bordered
            separator
            style="full-width"
            :class="$register ? undefined : 'bg-responsive-white'"
          >
            <!-- List of ticket types -->
            <q-item
              v-for="(userOrderData, index) in userOrderDatas"
              :key="userOrderData[0].id"
              :clickable="(ticketsAllowed || isOrderItemForUserData(userOrderData[0])) && isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage)"
              :class="{
                'text-responsive-grey': !ticketsAllowed,
                'q-px-sm column': $q.screen.lt.sm,
                'hidden': shouldHideUserOrderData(userOrderData),

              }"
              @click="toggleUserTicket(userOrderData)"
            >
              <q-item-section
                v-if="showMemberImages && $register"
                avatar
              >
                <q-img
                  class="self-center"
                  :src="getUserFromOrderData(userOrderData).photoUrl"
                  :ratio="1"
                  height="102px"
                  width="102px"
                />
              </q-item-section>
              <q-item-section
                :style="showMemberImages || ($q.screen.lt.lg && $q.screen.gt.xs) ? 'min-height: 70px;' : undefined"
                :class="$q.screen.lt.sm ? 'q-mx-xs' : undefined"
              >
                <div :class="$q.screen.lt.sm ? 'column' : 'row q-gutter-x-xs justify-between'">
                  <!-- Member info (name and chips) -->
                  <div
                    class="column items-start justify-center col"
                  >
                    <div
                      class="items-start justify-start"
                      :class="$q.screen.lt.sm ? 'row' : 'column'"
                    >
                      <!-- Member name -->
                      <div
                        class="ellipsis text-subtitle2"
                        style="font-size: 1rem; line-height: 1.2;"
                        :class="$q.screen.xs ? 'q-mx-xs col-auto' : undefined"
                        :style="{ 'max-width': $q.screen.xs ? '60px': '100px' }"
                        data-test-id="member-tickets-member-name"
                      >
                        {{ getUserFromOrderData(userOrderData).firstName !== '' ? getUserFromOrderData(userOrderData).firstName : getUserFromOrderData(userOrderData).name }}
                      </div>

                      <div
                        class="row justify-start q-gutter-x-sm"
                      >
                        <!-- Day pass label -->
                        <div
                          v-if="(order.user && order.user.id) === userOrderData[0].ticket.user.id && (order.user.dayPassDate || order.purchasingDayPass)"
                          class="text-caption text-responsive-grey-8 col-auto"
                          :class="{ 'parentheses': $q.screen.lt.sm }"
                        >
                          {{ $t('person.dayPassHolder') }}
                        </div>

                        <!-- Membership label -->
                        <template v-else-if="showMembershipLabel(userOrderData)">
                          <div
                            class="text-caption text-responsive-grey-8 col-auto"
                            :class="{ 'parentheses': $q.screen.lt.sm }"
                          >
                            <template v-if="!!userOrderData[0].ticket.user.membershipType && !!userOrderData[0].ticket.user.membershipType.name">
                              {{ (userOrderData[0].ticket.user.membershipType && userOrderData[0].ticket.user.membershipType.name) + (!!userOrderData[0].ticket.user.membershipTier ? (' - ' + userOrderData[0].ticket.user.membershipTier.name) : '') }}
                            </template>
                            {{ $t('membership.memberCount') }}
                            <q-icon
                              v-if="userOrderData[0].ticket.user.membership.state !== 'active' && $register"
                              class="col-auto"
                              name="cancel"
                              size="sm"
                              color="negative"
                            />
                          </div>
                        </template>
                      </div>
                    </div>
                    <div
                      v-if="$q.screen.gt.xs && !userOrderData[0].itemPackage && (hasMembersTicketVoucherNameForUserOrderData(userOrderData) || hasMembersTicketDiscountNameForUserOrderData(userOrderData))"
                      class="text-grey-7 text-subtitle text-italic"
                      style="font-size: 13px;"
                    >
                      <div
                        v-if="hasMembersTicketVoucherNameForUserOrderData(userOrderData)"
                        class="ellipsis"
                        style="max-width: 210px;"
                      >
                        {{ membersTicketVoucherName[getUserFromOrderData(userOrderData).id][userOrderData[0].ticket.ticketType.id] + ' available' }}
                      </div>
                      <div
                        v-if="hasMembersTicketDiscountNameForUserOrderData(userOrderData)"
                        class="ellipsis"
                        style="max-width: 210px;"
                      >
                        {{ membersTicketDiscountName[getUserFromOrderData(userOrderData).id][userOrderData[0].ticket.ticketType.id] + ' available' }}
                      </div>
                    </div>
                  </div>
                  <!-- Ticket options -->
                  <div
                    class="col column  justify-center"
                    :class="$q.screen.lt.lg ? 'items-start' : 'items-center'"
                    data-test-id="member-tickets-ticket-options-container"
                  >
                    <div
                      v-if="membersItemOptions[userOrderData[0].ticket.user.id].length <= 1"
                      class="ellipsis-2-lines q-px-xs"
                      :class="{
                        'text-responsive-grey-8': $q.screen.lt.sm,
                        'text-caption': $q.screen.lt.sm
                      }"
                      style="line-height: 1.2;"
                      square
                    >
                      {{ userOrderData[0].itemPackage ? userOrderData[0].itemPackage.name : userOrderData[0].ticket.ticketType.displayName }}
                      <q-tooltip v-if="$q.screen.lt.sm">
                        {{ userOrderData[0].itemPackage ? userOrderData[0].itemPackage.name : userOrderData[0].ticket.ticketType.displayName }}
                      </q-tooltip>
                    </div>
                    <div
                      v-else
                      class="full-width"
                      @click.stop
                    >
                      <q-select
                        :class="$q.screen.xs ? 'q-mt-sm q-mb-md ellipsis' : undefined"
                        :model-value="userOrderData[0].itemPackage ? userOrderData[0].itemPackage.name : userOrderData[0].ticket.ticketType.displayName"
                        :options="membersItemOptions[userOrderData[0].ticket.user.id]"
                        :option-disable="isMembersTicketTypeOrPackageDisabled"
                        dense
                        filled
                        :label="$t('ticket.viewTicketOptions')"
                        :hint="$q.screen.gt.xs ? membersItemOptions[userOrderData[0].ticket.user.id].length + ' ticket types available' : undefined"
                        data-test-id="member-tickets-ticket-options"
                        @popup-show="ticketTypeSelectOpen = true"
                        @popup-hide="ticketTypeSelectOpen = false"
                        @update:model-value="input => handleMemberItemSelect(index, input, userOrderData)"
                      >
                        <template #option="scope">
                          <q-item
                            v-bind="scope.itemProps"
                            v-on="scope.itemEvents"
                          >
                            <q-item-section>
                              {{ scope.opt.typeName === 'ItemPackage' ? scope.opt.name : scope.opt.displayName }}
                            </q-item-section>
                          </q-item>
                        </template>
                      </q-select>
                    </div>
                  </div>
                </div>
              </q-item-section>
              <q-item-section
                :side="$q.screen.gt.xs"
              >
                <div
                  class="row justify-between text-responsive"
                  :style="{ 'min-width': minWidth }"
                >
                  <div
                    class="col column justify-center"
                    :class="{
                      'q-pr-sm': $q.screen.lt.lg,
                      'items-center q-pl-md q-pr-lg': $q.screen.gt.md,
                      'items-end': $q.screen.lt.lg && $q.screen.gt.xs,
                      'items-start': $q.screen.lt.sm
                    }"
                  >
                    <div class="text-body1">
                      <span
                        v-if="(userOrderData[0].itemPackage && !getPackageRedemptionDisplayPrice(userOrderData[0].itemPackageId, getUserFromOrderData(userOrderData).id)) || (!userOrderData[0].itemPackage && !hasMembersTicketPriceForUserOrderData(userOrderData))"
                        style="font-size: 1.25rem; line-height: 1.25rem;"
                        class="text-weight-bolder"
                      >{{ $_.toUpper($t('pricing.free')) }}</span>
                      <div
                        v-else
                        class="column justify-center"
                        :class="$q.screen.lt.sm ? 'items-starts' : 'items-center'"
                      >
                        <div
                          class="text-weight-bolder"
                          :class="{ 'q-pt-xs': hasDiscount(userOrderData) }"
                          :style="{ 'font-size': $q.screen.xs ? '1.15rem': '1.2rem' }"
                          style="line-height: 1.2"
                        >
                          <span
                            v-if="getFinalPrice(userOrderData) === 0"
                          >{{ $_.toUpper($t('pricing.free')) }}</span>
                          <i18n-n
                            v-else
                            :value="getFinalPrice(userOrderData)"
                            format="currency"
                            scope="global"
                          />
                        </div>
                        <div
                          v-if="hasDiscount(userOrderData)"
                          class="row items-center justify-center"
                          style="font-size: 0.9rem; line-height: 1rem;"
                        >
                          <i18n-n
                            class="text-strike text-grey"
                            tag="span"
                            :value="getOriginalPrice(userOrderData)"
                            format="currency"
                            scope="global"
                          />
                          <div
                            class="q-pl-xs text-red"
                          >
                            <span>-</span>
                            <i18n-n
                              class="text-red"
                              tag="span"
                              :value="getDiscountAmount(userOrderData)"
                              format="currency"
                              scope="global"
                            />
                          </div>
                        </div>
                        <div
                          v-if="showTicketPriceBreakdown(userOrderData)"
                          class="text-caption text-responsive-grey-8 text-center"
                        >
                          (<i18n-n
                            tag="span"
                            :value="getFinalPrice(userOrderData) - getBookingFeeAmount(userOrderData)"
                            format="currency"
                          /> +
                          <i18n-n
                            tag="span"
                            :value="getBookingFeeAmount(userOrderData)"
                            format="currency"
                          />
                          {{ getBookingFeeName(userOrderData) }})
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="col-auto column items-center justify-center"
                    style="min-width: 50px; width: 100%;"
                    :style="{
                      'max-width': $q.screen.lt.sm ? '98px' : '108px',
                      'min-width': shouldShowAddAndRemoveButtons ? '50px' : undefined,
                      width: shouldShowAddAndRemoveButtons ? '100%' : undefined
                    }"
                  >
                    <q-btn
                      v-if="!isOrderItemForUserData(userOrderData[0]) || !ticketsAllowed || (!isOrderItemForUserData(userOrderData[0]) && !isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage)) || $register || shouldShowAddAndRemoveButtons"
                      class="col-auto row items-center"
                      style="max-width: 108px; min-width: 50px;"
                      :style="{ width: $q.screen.lt.lg ? undefined : '100%' }"
                      :label="!ticketsAllowed || (!isOrderItemForUserData(userOrderData[0]) && !isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage)) ? $t('status.unavailable') : isOrderItemForUserData(userOrderData[0]) ? $t('actions.remove') : $t('actions.add')"
                      :square="$register"
                      :rounded="!$register"
                      :color="(ticketsAllowed || isOrderItemForUserData(userOrderData[0]) && isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage)) && !$register ? 'responsive-grey-2' : undefined"
                      text-color="responsive"
                      :outline="(ticketsAllowed || isOrderItemForUserData(userOrderData[0]) && isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage)) && $register"
                      :flat="!ticketsAllowed || (!isOrderItemForUserData(userOrderData[0]) && !isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage))"
                      :disable="!ticketsAllowed || (!isOrderItemForUserData(userOrderData[0]) && !isTicketTypeOrPackageAvailable(userOrderData[0].ticket?.ticketType || userOrderData[0].itemPackage)) || (ticketLimitReached && !isOrderItemForUserData(userOrderData[0]))"
                      @click.stop="toggleUserTicket(userOrderData)"
                    >
                      <q-tooltip
                        v-if="ticketLimitReached"
                        :offset="[10, 10]"
                      >
                        {{ $t('showing.noMoreTicketsAvailableForShowing') }}
                      </q-tooltip>
                    </q-btn>
                    <q-item-label
                      v-else
                      class="d-flex justify-around"
                    >
                      <q-btn
                        class="col-auto"
                        icon="fal fa-trash-can"
                        color="responsive-grey-2"
                        text-color="responsive"
                        dense
                        round
                        @click.stop="toggleUserTicket(userOrderData)"
                      />

                      <q-chip
                        class="bg-transparent"
                        text-color="responsive"
                        flat
                        square
                        :size="$q.screen.lt.sm ? 'md' : 'lg'"
                      >
                        1
                      </q-chip>
                    </q-item-label>
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
          <div
            v-if="$consumer && redeemableVoucherTypes.length > 0 && currentUser && !combinedTicketSelection"
            class="text-caption text-center q-mt-sm"
          >
            <div
              class="text-bold text-primary"
            >
              {{ currentUser.points }} {{ currentCircuit.rewardsCurrencyName }}
              <q-icon
                style="margin-bottom: 2px;"
                name="info"
              >
                <q-tooltip>
                  <span
                    v-for="(voucherType, index) in redeemableVoucherTypes"
                    :key="voucherType.id"
                  >
                    <br v-if="index !== 0">{{ voucherType.name + ' (' + voucherType.pointsCost + ' ' + currentCircuit.rewardsCurrencyName + ')' }}
                  </span>
                </q-tooltip>
              </q-icon>
            </div>
            <span>{{ $t('checkout.redeemCurrencyPrompt', { rewardsCurrencyName: currentCircuit.rewardsCurrencyName }) }}.</span>
          </div>
        </template>
      </div>
      <select-tickets
        ref="selectTickets"
        :showing="showing"
        :user="user"
        :stale-showing="staleShowing"
        :tickets-allowed="ticketsAllowed"
        :ticket-order-items="ticketOrderItems"
        :package-pricing="packageRedemption"
        :initial-num-of-tickets-to-select="initialNumOfTicketsToSelect"
        :combined-ticket-selection="combinedTicketSelection"
        @request-authorization="callback => $emit('request-authorization', callback)"
      />

      <add-friends
        v-if="currentCircuit.allowFriends && user"
        :user="user"
      />
    </div>
  </div>
</template>

<script>

import { date } from 'quasar'
import { canSelectTicketTypeOrPackage, unassignedSelectedSeats } from 'src/models/order/CheckoutData'
import OrderItem, { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import Ticket from 'src/models/ticket/Ticket'
import { USER_TYPES } from 'src/models/user/User'
import AddFriends from 'src/modules/consumer/components/AddFriends'
import SelectTickets from 'src/modules/register/components/SelectTickets'
import MemberForm from 'src/modules/register/membership/MemberForm'

const { formatDate } = date

export default {
  name: 'SelectUsers',
  components: {
    AddFriends,
    MemberForm,
    SelectTickets
  },
  inject: ['order', 'checkoutData'],
  props: {
    showing: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: false,
      default: null
    },
    staleShowing: {
      type: Object,
      required: false,
      default () {
        return null
      }
    },
    combinedTicketSelection: {
      type: Boolean,
      default: false
    },
    selectedSeat: {
      type: String,
      default: null
    },
    showCurrentSelectedSeatInfo: {
      type: Boolean,
      default: false
    }
  },
  emits: ['find-user', 'modify-membership', 'fix-pending-membership', 'request-authorization'],
  data () {
    let initialNumOfTicketsToSelect = 0

    if (this.checkoutData) {
      initialNumOfTicketsToSelect = unassignedSelectedSeats(this.order, this.showing.id, this.checkoutData.selectedSeatsForShowingId(this.showing.id)).length
    }

    return {
      formatDate,

      toggledMemberTicket: false,

      showMemberForm: false,
      editingUser: null,
      addDayPassLoading: false,

      showAuthorizedPurchaserDialog: false,
      authorizedPurchaserLoading: false,

      userOrderDatas: [],

      transientOrder: null,
      transientOrderItems: {
        member: {},
        packages: [],
        tickets: []
      },

      transientOrderCurrentJobId: 0,

      ticketLimitReached: false,
      initialNumOfTicketsToSelect,
      ticketTypeSelectOpen: false
    }
  },
  configuration: ['currentSite', 'currentCircuit', 'membershipTypes', 'voucherTypes'],
  context: ['currentUser'],
  computed: {
    membersItemOptions () {
      const membersItems = {}

      const fallbackTicketType = _.find(
        this.showing.ticketTypes(),
        ticketType => !ticketType.membershipType && ticketType.availableOnline && (
          _.isNil(this.checkoutData) ||
          canSelectTicketTypeOrPackage(this.order, this.showing, ticketType, this.checkoutData.selectedSeatsForShowingId(this.showing.id), this.currentSite.consumerConfig.showTicketFirstCheckoutFlow)
        )
      )
      const fallbackPackage = _.find(
        this.showing.packages('guest'),
        pack => pack.availableOnline && (
          _.isNil(this.checkoutData) ||
          canSelectTicketTypeOrPackage(this.order, this.showing, pack, this.checkoutData.selectedSeatsForShowingId(this.showing.id), this.currentSite.consumerConfig.showTicketFirstCheckoutFlow)
        )
      )

      _.each(this.userOrderDatas, userOrderDatas => {
        const userOrderData = userOrderDatas[0]
        const ticketOptions = this.showing.memberTicketTypes(userOrderData.ticket.user, true)
        const packageOptions = this.showing.memberPackages(userOrderData.ticket.user, true)

        const sortedOptions = _(ticketOptions)
          .concat(packageOptions)
          .sortBy(([order, ticketOrPackage]) => order)
          .map(([order, ticketOrPackage]) => {
            if (
              _.isNil(this.checkoutData) ||
              canSelectTicketTypeOrPackage(this.order, this.showing, ticketOrPackage, this.checkoutData.selectedSeatsForShowingId(this.showing.id), this.currentSite.consumerConfig.showTicketFirstCheckoutFlow)
            ) return ticketOrPackage

            return null
          })
          .compact()
          .value()
        membersItems[userOrderData.ticket.user.id] = sortedOptions

        if (membersItems[userOrderData.ticket.user.id].length === 0) {
          if (fallbackTicketType) membersItems[userOrderData.ticket.user.id].push(fallbackTicketType)
          if (fallbackPackage) membersItems[userOrderData.ticket.user.id].push(fallbackPackage)
        }
      })

      return membersItems
    },
    allTicketTypes () {
      return this.currentShowing.ticketTypes()
    },
    ticketTypes () {
      return this.currentShowing.ticketTypes('guest')
    },
    packages () {
      return this.currentShowing.packages('guest')
    },
    allPackages () {
      return this.currentShowing.packages()
    },
    ticketOrderItems () {
      if (this.transientOrderCurrentJobId === null) return

      return this.transientOrderItems.tickets
    },

    packageOrderItems () {
      if (this.transientOrderCurrentJobId === null) return

      return this.transientOrderItems.packages
    },
    packageRedemption () {
      const prices = {}
      _.each(this.packageOrderItems, packageOrderItem => {
        prices[packageOrderItem.itemPackageId] ||= { displayPrice: 0, displayTotal: 0, discountAmount: 0, bookingFeeAmount: 0, bookingFeeNames: [] }
        prices[packageOrderItem.itemPackageId].displayTotal += packageOrderItem.displayPrice - packageOrderItem.displayRedemptionTotal
        prices[packageOrderItem.itemPackageId].displayPrice += packageOrderItem.displayPrice
        prices[packageOrderItem.itemPackageId].discountAmount += packageOrderItem.displayRedemptionTotal
        prices[packageOrderItem.itemPackageId].bookingFeeAmount += packageOrderItem.includeBookingFeeInPrice ? packageOrderItem.bookingFeeDisplayPrice : 0
        const bookingFeeName = (packageOrderItem.bookingFeeType || packageOrderItem.localBookingFeeType)?.name
        if (!_.isNil(bookingFeeName)) {
          prices[packageOrderItem.itemPackageId].bookingFeeNames = _.union(prices[packageOrderItem.itemPackageId].bookingFeeNames, [bookingFeeName])
        }
      })
      return prices
    },
    membersOrderItems () {
      if (this.transientOrderCurrentJobId === null) return

      return this.transientOrderItems.member
    },
    membersTicketPrice () {
      const prices = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        prices[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          prices[memberId][ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.displayPrice
        })
      })
      return prices
    },
    membersTicketRedemption () {
      const prices = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        prices[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          prices[memberId][ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.displayRedemptionTotal
        })
      })
      return prices
    },
    membersTicketVoucherName () {
      const voucherNames = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        voucherNames[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          voucherNames[memberId][ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.displayVoucherName
        })
      })
      return voucherNames
    },
    membersTicketDiscountName () {
      const discountNames = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        discountNames[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          discountNames[memberId][ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.displayDiscountName
        })
      })
      return discountNames
    },
    membersTicketBookingFeePrice () {
      const bookingFeePrices = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        bookingFeePrices[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          bookingFeePrices[memberId][ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.bookingFeeDisplayPrice
        })
      })
      return bookingFeePrices
    },
    membersTicketBookingFeeIncluded () {
      const bookingFeeIncluded = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        bookingFeeIncluded[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          bookingFeeIncluded[memberId][ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.includeBookingFeeInPrice
        })
      })
      return bookingFeeIncluded
    },
    membersTicketBookingFeeName () {
      const bookingFeeNames = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        bookingFeeNames[memberId] = {}
        _.each(this.membersOrderItems[memberId].tickets, ticketOrderItem => {
          bookingFeeNames[memberId][ticketOrderItem.ticket.ticketType.id] = (ticketOrderItem.orderItemBookingFee?.bookingFeeType || ticketOrderItem.localBookingFeeType)?.name
        })
      })
      return bookingFeeNames
    },
    membersPackageRedemption () {
      const prices = {}
      _.each(_.keys(this.membersOrderItems), memberId => {
        prices[memberId] = {}
        _.each(this.membersOrderItems[memberId].packages, packageOrderItem => {
          prices[memberId][packageOrderItem.itemPackageId] ||= { displayPrice: 0, displayTotal: 0, discountAmount: 0, bookingFeeAmount: 0, bookingFeeNames: [] }
          prices[memberId][packageOrderItem.itemPackageId].displayTotal += packageOrderItem.displayPrice - packageOrderItem.displayRedemptionTotal
          prices[memberId][packageOrderItem.itemPackageId].displayPrice += packageOrderItem.displayPrice
          prices[memberId][packageOrderItem.itemPackageId].discountAmount += packageOrderItem.displayRedemptionTotal
          prices[memberId][packageOrderItem.itemPackageId].bookingFeeAmount += packageOrderItem.includeBookingFeeInPrice ? packageOrderItem.bookingFeeDisplayPrice : 0
          const bookingFeeName = (packageOrderItem.bookingFeeType || packageOrderItem.localBookingFeeType)?.name
          if (!_.isNil(bookingFeeName)) {
            prices[memberId][packageOrderItem.itemPackageId].bookingFeeNames = _.union(prices[memberId][packageOrderItem.itemPackageId].bookingFeeNames, [bookingFeeName])
          }
        })
      })
      return prices
    },
    ticketsAllowed () {
      const showing = this.currentShowing
      return (
        !this.currentSite.membershipRequired ||
        (showing && showing.allowWithoutMembership) ||
        (this.order.user && (
          (this.order.user && this.order.user.membership && this.order.user.membership.effective && this.order.user.unlimitedMembershipTicketVoucherTypes.length > 0) ||
          (this.order.purchasingDayPass && (!this.order.showing || formatDate(this.order.showing.time, 'YYYY-MM-DD') === formatDate(showing.time, 'YYYY-MM-DD'))) ||
          (this.order.user.dayPassDate && formatDate(this.order.user.dayPassDate, 'YYYY-MM-DD') === formatDate(showing.time, 'YYYY-MM-DD'))
        ))
      )
    },
    membershipTypesAvailable () {
      return !!_.find(this.membershipTypes.data, membershipType => {
        return !membershipType.default && !membershipType.hide && membershipType.availableOnline
      })
    },
    showMemberImages () {
      return this.$register && !!this.order.user && !_.every(this.userOrderDatas, userOrderData => {
        return !this.getUserFromOrderData(userOrderData).hasPhoto
      })
    },
    hasMemberTickets () {
      return _.some(this.order.orderItems.data, orderItem => {
        if (orderItem.type !== ORDER_ITEM_TYPES.TICKET) return false

        return !!orderItem.ticket.user
      })
    },
    redeemableVoucherTypes () {
      if (!this.currentUser) return []

      // Find reward vouchers with a point cost less than or equal to the user's current points
      const voucherTypes = _.filter(this.voucherTypes.data, voucherType => {
        return voucherType.isReward && voucherType.pointsCost <= this.currentUser.points
      })
      return _.sortBy(voucherTypes, 'pointsCost')
    },
    minWidth () {
      if (this.$kiosk) return '300px'
      if (this.$q.screen.xs) return '170px'
      if (this.$q.screen.lt.lg) return '200px'
      return '300px'
    },
    currentShowing () {
      if (this.showing?.loading && this.staleShowing) {
        return this.staleShowing
      } else {
        return this.showing
      }
    },
    shouldShowAddAndRemoveButtons () {
      if (!this.showing?.reservedSeating) return true

      return this.initialNumOfTicketsToSelect !== 1
    }
  },
  watch: {
    'order.user' () {
      if (this.order?.user && !this.order.user?.loading) {
        this.computeUserOrderDatas()
        this.refreshTransientOrder(true)
      }
    },
    'order.loading' () {
      if (!this.order.loading) {
        this.refreshTransientOrder(true)
      }
    },
    'user.loading' () {
      if (!this.user?.loading) {
        this.computeUserOrderDatas()
        this.refreshTransientOrder(true)
      }
    },
    'order.availableRedemptions' () {
      if (!this.user) {
        this.refreshTransientOrder(true)
      }
    },
    'showing.loading': {
      handler () {
        if (!this.showing.loading) {
          this.computeUserOrderDatas()
          this.order.currentShowing = this.showing
          this.refreshTransientOrder(true)
          this.ticketLimitReached = this.order.isMaxTicketLimitReached(this.showing)
        }
      },
      immediate: true
    },
    'order.orderItems.data.length' () {
      this.refreshTransientOrder()
      this.ticketLimitReached = this.order.isMaxTicketLimitReached(this.showing)
    },
    // Handle the case where the order item objects have not changed and doesn't trigger reactivity,
    // but the items' price have changed due to applied/removed redemptions.
    'order.totalOwed' () {
      this.refreshTransientOrder()
    }
  },
  async mounted () {
    if (!this.$register && !this.$kiosk) {
      if (this.currentUser) {
        const orderUser = this.order.setRelated('user', { id: this.currentUser.id })
        await orderUser.fetch(true)
      } else {
        this.order.user = this.order.purchasingDayPass ? this.order.dayPassUser : null
      }
    }
    this.computeUserOrderDatas()
  },
  created () {
    _.extend(this, { USER_TYPES, canSelectTicketTypeOrPackage })
  },
  unmounted () {
    _.each(_.flatten(this.userOrderDatas), oi => oi.destroy())
    // Here we update the current job id, in case recomputation in progress, this will trigger destroy
    this.transientOrderCurrentJobId++
    this.resetTransientOrder()
  },
  methods: {
    // Determines whether or not to show a membership label for a specific user
    showMembershipLabel (userOrderData) {
      return userOrderData[0].ticket.user.membership &&
        !userOrderData[0].ticket.user.membershipType.hide &&
        userOrderData[0].ticket.user.membership.state === 'active'
    },
    showTicketPriceBreakdown (userOrderData) {
      if (this.$register || this.$kiosk) return false

      const userData = this.getUserFromOrderData(userOrderData)
      const userId = userData.id

      if (userOrderData[0].itemPackage) {
        return this.membersPackageRedemption[userId]?.[userOrderData[0].itemPackageId]?.bookingFeeAmount > 0
      } else {
        return this.membersTicketBookingFeeIncluded[userId][userOrderData[0].ticket.ticketType.id] && this.membersTicketBookingFeePrice[userId][userOrderData[0].ticket.ticketType.id] > 0
      }
    },
    computeUserOrderDatas () {
      _.each(_.flatten(this.userOrderDatas), oi => oi.destroy())
      if (this.user && !this.user?.loading && this.showing && !this.showing?.loading) {
        this.userOrderDatas = this.order.initializeDataOrderForShowing(this.user, this.showing, this.checkoutData)
      } else {
        this.userOrderDatas = []
      }
    },
    getPackageRedemptionDisplayPrice (itemPackageId, userId = null) {
      const memberPrice = this.membersPackageRedemption[userId]?.[itemPackageId]?.displayTotal
      const nonMemberPrice = this.packageRedemption[itemPackageId]?.displayPrice
      return nonMemberPrice === undefined ? memberPrice : nonMemberPrice
    },
    getOriginalPrice (userOrderData) {
      const userData = this.getUserFromOrderData(userOrderData)
      const userId = userData.id

      if (userOrderData[0].itemPackage) {
        return this.getPackageRedemptionDisplayPrice(userOrderData[0].itemPackageId, userId)
      } else {
        return this.membersTicketPrice[userId][userOrderData[0].ticket.ticketType.id]
      }
    },
    getDiscountAmount (userOrderData) {
      const userData = this.getUserFromOrderData(userOrderData)
      const userId = userData.id

      if (userOrderData[0].itemPackage) {
        return this.getPackageRedemptionDisplayPrice(userOrderData[0].itemPackageId, userId) -
             this.membersPackageRedemption[userId]?.[userOrderData[0].itemPackageId]?.displayPrice
      } else {
        return this.membersTicketRedemption[userId][userOrderData[0].ticket.ticketType.id]
      }
    },
    getBookingFeeAmount (userOrderData) {
      const userData = this.getUserFromOrderData(userOrderData)
      const userId = userData.id

      if (userOrderData[0].itemPackage) {
        return this.membersPackageRedemption[userId]?.[userOrderData[0].itemPackageId]?.bookingFeeAmount
      } else {
        return this.membersTicketBookingFeePrice[userId][userOrderData[0].ticket.ticketType.id]
      }
    },
    getBookingFeeName (userOrderData) {
      const userData = this.getUserFromOrderData(userOrderData)
      const userId = userData.id

      if (userOrderData[0].itemPackage) {
        return this.membersPackageRedemption[userId]?.[userOrderData[0].itemPackageId]?.bookingFeeNames?.join(', ')
      } else {
        return this.membersTicketBookingFeeName[userId][userOrderData[0].ticket.ticketType.id]
      }
    },
    getFinalPrice (userOrderData) {
      const userData = this.getUserFromOrderData(userOrderData)
      if (!userData) return 0

      const userId = userData.id

      if (userOrderData[0].itemPackage) {
        return this.membersPackageRedemption?.[userId]?.[userOrderData[0].itemPackageId]?.displayTotal || 0
      } else {
        const ticketTypeId = userOrderData[0].ticket?.ticketType?.id
        if (!ticketTypeId || !this.membersTicketPrice?.[userId]?.[ticketTypeId]) return 0

        const originalPrice = this.membersTicketPrice[userId][ticketTypeId] || 0
        const redemption = this.membersTicketRedemption?.[userId]?.[ticketTypeId] || 0

        return originalPrice - redemption
      }
    },
    hasDiscount (userOrderData) {
      return this.getDiscountAmount(userOrderData) > 0
    },
    refreshTransientOrder (resetOrder = false) {
      // if some thing is loading, it's going to loaded soon which will cause a recompute so don't bother with recompute now
      if (this.showing.loading || this.user?.loading || this.order?.loading) return

      if (resetOrder) {
        this.resetTransientOrder()
      }

      const transientOrderCurrentJobId = ++this.transientOrderCurrentJobId

      let transientOrder = this.transientOrder
      const transientOrderItems = {
        packages: [],
        tickets: [],
        member: this.transientOrderItems.member
      }

      if (!transientOrder) {
        // We don't have an existing transient order - we need to create one.

        // We need to reset member tickets as the user may have changed.
        transientOrderItems.member = {}
        // Copy order only when transient order doesn't exist as this is compute heavy.
        transientOrder = this.order.copy(true)
        // Set copied order to transient to safeguard against unwanted operations.
        transientOrder.transient = true

        // Add all tickets types that exist for this showing to transient order
        _.each(this.ticketTypes, ticketType => {
          transientOrder.orderItems.add(new OrderItem({
            member: { name: 'Your Guest' },
            type: 'ticket',
            ticket: {
              showing: this.showing,
              ticketTypeId: ticketType.id
            },
            transient: true
          }))
        })

        // Add all packages that exist for this showing to transient order
        _.each(this.packages, pack => {
          transientOrder.addItemPackage(pack, this.showing, null, true)
        })

        // Add all member tickets that exist for this showing to transient order
        _.each(this.userOrderDatas, userOrderDatas => {
          const userOrderData = userOrderDatas[0]
          const member = userOrderData.ticket.user
          transientOrderItems.member[member.id] = { packages: [], tickets: [] }

          if (userOrderData.itemPackage) {
            transientOrder.addItemPackage(userOrderData.itemPackage, this.showing, member, true)
          } else {
            transientOrder.orderItems.add(new OrderItem({
              member,
              type: 'ticket',
              ticket: {
                user: member,
                showing: this.showing,
                ticketTypeId: userOrderData.ticket.ticketTypeId
              },
              transient: true
            }))
          }
        })
      }

      // We need to work out if any order items have been removed from the real order and also remove these from the transient order
      const nonTransientOrderItems = _.filter(transientOrder?.orderItems?.data, transientOrderItem => transientOrderItem.transient === false && transientOrderItem.options.copiedFromId)
      const itemsToRemoveIds = _.difference(_.map(nonTransientOrderItems, oi => oi.options.copiedFromId) || [], _.map(this.order?.orderItems?.data, 'id') || [])
      const itemsToRemove = _.filter(transientOrder?.orderItems?.data, oi => _.includes(itemsToRemoveIds, oi.options.copiedFromId))
      _.each(itemsToRemove, item => {
        transientOrder.orderItems.remove(item)
        item.destroy()
      })
      // We need to work out if any order items have been added to the real order so we can also add these to the transient order
      const itemsToAddIds = _.difference(_.map(this.order?.orderItems?.data, 'id') || [], _.map(transientOrder?.orderItems?.data, oi => oi.options.copiedFromId || oi.id) || [])
      const itemsToAdd = _.filter(this.order?.orderItems?.data, oi => _.includes(itemsToAddIds, oi.id))
      _.each(itemsToAdd, item => {
        const copiedItem = item.copy(true)
        const tempOrder = copiedItem.data.order
        copiedItem.data.order = null
        transientOrder.orderItems.add(copiedItem)
        if (tempOrder !== this.order) {
          tempOrder?.destroy()
        }
      })

      // Reset discounts
      // Re add discounts
      _.each(transientOrder.orderItems.data, transientOrderItem => {
        transientOrderItem.discount = null

        if (transientOrderItem.transient) return

        const matchingItem = _.find(this.order.orderItems.data, oi => oi.id === transientOrderItem.options.copiedFromId || oi.id === transientOrderItem.id)

        if (!matchingItem) return

        transientOrderItem.discountUserId = matchingItem.discountUserId

        transientOrderItem.discountGroupKey = matchingItem.discountGroupKey

        if (matchingItem.discount === transientOrderItem.discount) return

        transientOrderItem.discountId = matchingItem.discountId
      })

      // Call apply transient redemptions so that discounts/vouchers are applied before we store the price.
      transientOrder.setupCurrentMemberDiscounts()
      transientOrder.applyTransientRedemptions()

      // Iterate over each order item so that we can add these transientOrderItems which are used to fetch pricing.
      _.each(transientOrder.orderItems.data, orderItem => {
        if (!orderItem.transient) return

        if (orderItem.itemPackage) {
          if (orderItem.ticket?.user || orderItem.itemOrderItem?.user) {
            if (!_.includes(transientOrderItems.member[(orderItem.ticket?.user || orderItem.itemOrderItem?.user)?.id]?.packages, orderItem)) {
              transientOrderItems.member[(orderItem.ticket?.user || orderItem.itemOrderItem?.user)?.id]?.packages.push(orderItem)
            }
          } else {
            transientOrderItems.packages.push(orderItem)
          }
        } else if (orderItem.ticket) {
          if (orderItem.ticket?.user) {
            if (!_.includes(transientOrderItems.member[orderItem.ticket?.user.id].tickets, orderItem)) {
              transientOrderItems.member[orderItem.ticket?.user.id].tickets.push(orderItem)
            }
          } else {
            transientOrderItems.tickets.push(orderItem)
          }
        }
      })
      // Ensure no recompute has started since - e.g. another recompute started post our execution
      if (transientOrderCurrentJobId === this.transientOrderCurrentJobId) {
        this.transientOrder = transientOrder
        this.transientOrderItems = transientOrderItems
      } else {
        transientOrder?.destroy()
      }
    },
    resetTransientOrder () {
      // Set transient order to null, as we want to recompute user tickets
      this.transientOrder?.destroy()
      this.transientOrder = null
    },
    getUserFromOrderData (userOrderData) {
      return userOrderData[0].ticket?.user || userOrderData[0].itemOrderItem?.user
    },
    orderItemForUserData (userOrderData) {
      const userTicket = _.find(this.order.orderItems.data, (orderItem) => {
        return orderItem.type === 'ticket' &&
          orderItem.ticket.showing.id === this.showing.id &&
          orderItem.ticket.user && orderItem.ticket.user.id === userOrderData.ticket.user.id &&
          orderItem.isRemovable
      })

      if (!userTicket) return []

      if (!userTicket.itemPackage) return [userTicket]

      return _.filter(this.order.orderItems.data, item => item.itemPackage === userTicket.itemPackage && (item.ticket?.user?.id === userOrderData.ticket.user.id || item.itemOrderItem?.user?.id === userOrderData.ticket.user.id))
    },
    isOrderItemForUserData (userOrderData) {
      return this.orderItemForUserData(userOrderData).length !== 0
    },
    toggleUserTicket (userOrderDatas) {
      if (this.ticketTypeSelectOpen) return
      if (!this.toggledMemberTicket) this.$refs.selectTickets.toggleExpand(true)
      this.toggledMemberTicket = true

      const userOrderData = userOrderDatas[0]
      const member = _.find(userOrderDatas, orderItem => orderItem?.ticket.user)?.ticket?.user
      if (member.type === USER_TYPES.GUEST && !this.currentCircuit.allowGuests) {
        this.editingUser = member
        this.$refs.memberForm.show(member)
        this.showMemberForm = true
        return
      }

      if (this.isOrderItemForUserData(userOrderData)) {
        _.each(this.orderItemForUserData(userOrderData), orderItem => this.order.orderItems.remove(orderItem))
      } else {
        if (userOrderData.itemPackage) {
          this.order.addItemPackage(_.find(this.allPackages, { id: userOrderData.itemPackageId }), this.showing, member)
        } else {
          this.order.orderItems.add(userOrderData.copy())
        }
        this.order.applyRedemptions()
      }
    },
    handleMemberItemSelect (index, input, userOrderDatas, authorized = false) {
      this.ticketTypeSelectOpen = false

      const userOrderData = userOrderDatas[0]
      const member = _.find(userOrderDatas, orderItem => orderItem?.ticket.user)?.ticket?.user

      let selectedUserOrderDatas = []
      if (input.typeName === 'ItemPackage') {
        selectedUserOrderDatas = this.order.createItemPackages(input, this.showing, member)
      } else if (input.typeName === 'TicketType') {
        if (this.$register && !this.currentUser.admin && !authorized && input.requiresAuthorization) {
          this.$emit('request-authorization', () => this.handleMemberItemSelect(index, input, userOrderDatas, true))
          return
        }
        const ticket = new Ticket({
          user: member,
          showing: this.showing,
          ticketTypeId: input?.id,
          tickets: this.order.getMarathonTicketsForShowing(this.showing, input, member)
        })
        selectedUserOrderDatas = [new OrderItem({
          type: 'ticket',
          ticket
        })]
      }

      if (this.isOrderItemForUserData(userOrderData)) {
        _.each(this.orderItemForUserData(userOrderData), orderItem => this.order.orderItems.remove(orderItem))
        if (input.typeName === 'ItemPackage') {
          this.order.addItemPackage(input, this.showing, member)
        } else if (input.typeName === 'TicketType') {
          this.order.orderItems.add(selectedUserOrderDatas[0].copy())
        }
        this.order.applyRedemptions()
      }

      _.each(userOrderDatas, userOrderData => { userOrderData.destroy() })
      this.userOrderDatas[index] = selectedUserOrderDatas

      this.refreshTransientOrder(true)
    },
    async addDayPass () {
      this.addDayPassLoading = true

      await this.order.addDayPass()
      const dayPassOrderItemData = _.filter(_.flatten(this.order.initializeDataOrderForShowing(this.user, this.showing)), orderItem => orderItem?.ticket?.user?.id === this.order.user.id || orderItem?.itemOrderItem?.user?.id === this.order.user.id)
      _.each(dayPassOrderItemData, orderItemData => this.order.orderItems.add(orderItemData))
      this.computeUserOrderDatas()

      this.addDayPassLoading = false
    },
    memberFormApplyChanges () {
      this.editingUser.save()
      this.showMemberForm = false
    },
    onlyItemsForCurrentSite (itemPackageItem) {
      // An itemPackage can contain items that are linked to ticketTypes or
      // itemVariations that belong to a different site within the parent
      // circuit. We don't want to display those items, so we use the ticketType
      // and itemVariation properties to filter them out. Those properties
      // resolve to null because their references don't exist in the client
      // config, which is scoped to the current site.
      return itemPackageItem.ticketType || itemPackageItem.itemVariation
    },
    hasMembersTicketVoucherNameForUserOrderData (userOrderData) {
      return this.membersTicketVoucherName[this.getUserFromOrderData(userOrderData).id] && this.membersTicketVoucherName[this.getUserFromOrderData(userOrderData).id][userOrderData[0].ticket.ticketType.id]
    },
    hasMembersTicketDiscountNameForUserOrderData (userOrderData) {
      return this.membersTicketDiscountName[this.getUserFromOrderData(userOrderData).id] && this.membersTicketDiscountName[this.getUserFromOrderData(userOrderData).id][userOrderData[0].ticket.ticketType.id]
    },
    hasMembersTicketPriceForUserOrderData (userOrderData) {
      return this.membersTicketPrice[this.getUserFromOrderData(userOrderData).id] && this.membersTicketPrice[this.getUserFromOrderData(userOrderData).id][userOrderData[0].ticket.ticketType.id]
    },
    shouldHideUserOrderData (userOrderData) {
      if (!this.showing?.reservedSeating) return false

      const hasTicketTypeOrItemPackageAvailable = !_.isEmpty(this.membersItemOptions[userOrderData[0].ticket.user.id])

      if (!hasTicketTypeOrItemPackageAvailable) {
        return true
      } else if (this.shouldShowAddAndRemoveButtons) {
        return false
      } else {
        // If add/remove buttons are hidden, we're selecting a single seat. If
        // there's already an order item for the user order data, it's
        // unavailable to be selected again.
        return this.isOrderItemForUserData(userOrderData[0])
      }
    },
    isMembersTicketTypeOrPackageDisabled (ticketTypeOrPackage) {
      const isSelectable = !_.isNil(this.checkoutData) && canSelectTicketTypeOrPackage(this.order, this.showing, ticketTypeOrPackage, this.checkoutData.selectedSeatsForShowingId(this.showing.id), this.currentSite.consumerConfig.showTicketFirstCheckoutFlow)
      if (!isSelectable) return false

      return !this.isTicketTypeOrPackageAvailable(ticketTypeOrPackage)
    },
    isTicketTypeOrPackageAvailable (ticketTypeOrPackage) {
      if (!this.ticketsAllowed) return false

      if (ticketTypeOrPackage.typeName === 'ItemPackage') {
        return this.order.canAddItemPackage(ticketTypeOrPackage, this.showing)
      } else {
        return this.order.ticketsRemaining(this.showing, ticketTypeOrPackage) > 0
      }
    }
  }
}
</script>
